import { useContext, useEffect, useState } from "react";
import { StepNextButton } from "../../../components";
import { StepperContext, useLoanApplication } from "../../../contexts";
import {
  LoanApplicationStage,
  LoanProductType,
  ShareholderKYCApplicationStatus,
} from "../../../hooks/api/types";
import {
  InvoiceReviewSection,
  BusinessInformationReviewSection,
  ConnectedBanksReviewSection,
  DocumentsReviewSection,
  LoanInformationReviewSection,
  OwnersInformationReviewSection,
} from "./sections";
import { useIFrameEvents, useLoanMetrics } from "../../../hooks";
import { SumSubKycModal } from "../OwnersInformation";
import ShareHolderDialogModal from "./ShareHolderDialogModal";
import { useLoanProductConfigFlags } from "../../../hooks/useLoanProductConfigFlags";
import { MerchantIdInformationSection } from "./MerchantIdInformationSection";
import VendorReviewSection from "./sections/VendorReviewSection";

type Props = {};

const ApplicationReview = (props: Props) => {
  const { nextStep } = useContext(StepperContext);
  const {
    actions: { update, refetch },
    state,
  } = useLoanApplication();
  const [kycModalOpen, setKycModalOpen] = useState(false);
  const { postLoanApplicationCompletedEvent } = useIFrameEvents();
  const [shareHolderMessageModalOpen, setShareHolderMessageModalOpen] =
    useState(false);
  const { loanAmount } = useLoanMetrics();

  const {
    disableBankStatementsUpload,
    disableInvoiceUpload,
    showMerchantIdStep,
    enableVendorScreen,
  } = useLoanProductConfigFlags(state.loanType, loanAmount);

  useEffect(() => {
    refetch();
  }, []);

  const handleNextStep = async () => {
    if (
      !state?.individualShareholders?.some((s) => s.hasPOA) &&
      !state?.companyShareholders?.some((s) => s.hasPOA)
    ) {
      setShareHolderMessageModalOpen(true);
    } else {
      // Determine if KYC flow already sufficiently progressed to auto submit
      const kycStatus = state.applicant.latestKycApplication?.status;
      const canAutoSubmit =
        [
          ShareholderKYCApplicationStatus.IN_PROGRESS,
          ShareholderKYCApplicationStatus.COMPLETED,
        ].includes(kycStatus) ||
        (!state.applicant.hasPOA && !state.applicant.isStakeholder);

      if (canAutoSubmit) {
        await update({
          ...state,
          stage: LoanApplicationStage.SUCCESS,
        })
          .then(() => {
            nextStep();
            postLoanApplicationCompletedEvent({
              loanApplicationId: state.id,
            });
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        // Show KYC modal (retry) but user now also has a separate button to submit anyway
        setKycModalOpen(true);
      }
    }
  };

  // Allow user to submit application even if KYC not completed/in progress.
  const handleForceSubmit = async () => {
    await update({
      ...state,
      stage: LoanApplicationStage.SUCCESS,
    })
      .then(() => {
        nextStep();
        postLoanApplicationCompletedEvent({ loanApplicationId: state.id });
      })
      .catch((err) => console.log(err));
  };

  return (
    <div className="cx-max-w-[960px] cx-w-full cx-flex cx-flex-col cx-items-center">
      <div className="cx-text-3xl cx-font-bold cx-text-center">
        Review your application
      </div>
      <div className="cx-text-text-secondary cx-text-base cx-text-center cx-mt-2">
        Carefully review all of your details before submitting the application
      </div>
      <LoanInformationReviewSection />
      {(state.loanType === LoanProductType.INVOICE_DISCOUNTING ||
        state.loanType === LoanProductType.PAYABLE_FINANCING) &&
        !disableInvoiceUpload && <InvoiceReviewSection />}
      <BusinessInformationReviewSection />
      <OwnersInformationReviewSection />
      {showMerchantIdStep && <MerchantIdInformationSection />}
      {!disableBankStatementsUpload && <ConnectedBanksReviewSection />}
      {enableVendorScreen && <VendorReviewSection />}
      <DocumentsReviewSection />
      <div className="cx-w-full cx-flex cx-flex-col cx-items-center cx-gap-4 cx-max-w-[400px] cx-mt-10 cx-mb-10">
        <StepNextButton
          arrow={"right"}
          label={"Complete\u00A0\u00A0"}
          loading={state.updating}
          onClick={handleForceSubmit}
        />
        {(![
          ShareholderKYCApplicationStatus.IN_PROGRESS,
          ShareholderKYCApplicationStatus.COMPLETED,
        ].includes(state.applicant.latestKycApplication?.status) ||
          state.applicant.latestKycApplication?.kycApplication?.reviewAnswer ==
            "RED") &&
          (state.applicant.hasPOA || state.applicant.isStakeholder) && (
            <StepNextButton
              arrow={"right"}
              label={"Retry Verification\u00A0\u00A0"}
              loading={false}
              onClick={() => setKycModalOpen(true)}
            />
          )}
      </div>
      <SumSubKycModal
        isOpen={kycModalOpen}
        onClose={() => setKycModalOpen(false)}
        onPrev={() => setKycModalOpen(false)}
        onSubmit={handleNextStep}
      />
      <ShareHolderDialogModal
        isOpen={shareHolderMessageModalOpen}
        onClose={() => setShareHolderMessageModalOpen(false)}
      />
    </div>
  );
};

export default ApplicationReview;
